import { Injectable, Injector, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';

import { ViewerIdentityService } from '../../core/services/viewer-identity.service';
import { ProfileDataService } from '../../core/services/profile-data.service';
import { SupportSessionService } from '../../core/services/support-session.service';
import type { IntroTrigger } from './settings-intro.model';
import { readIntroSeen, writeIntroSeen, type IntroSeenHow } from './settings-intro-store';

/** The replay route: it serves the Overview, and the intro opens over it. */
export const INTRO_ROUTE = '/welcome';

/**
 * Where the intro may open by itself. Never on the sign-in screens, the
 * no-access page, the operator console (Ricoh staff across customers), the
 * Microsoft consent hand-off or a chromeless player; and not on `/welcome`,
 * which opens it on purpose.
 */
export function introMayOpenAt(url: string): boolean {
  const path = url.split(/[?#]/)[0];
  if (path === INTRO_ROUTE) return false;
  return !/^\/(no-access|sign-in-problem|operator|settings\/integrations|signage|games|content|live-events|events|preview|catering|v)(\/|$)/.test(
    path,
  );
}

/** Everything the first-run decision reads, so the rule is one pure function. */
export interface IntroFirstRunInput {
  readonly userId: string | null;
  /** The viewer holds an admin action (the same test `adminGuard` makes). */
  readonly isAdmin: boolean;
  /** The settled URL, or null before the first navigation ends. */
  readonly url: string | null;
  /** A Ricoh support session: the operator is a visitor in this customer, not its admin. */
  readonly supportSession: boolean;
  readonly seen: boolean;
  /**
   * An automated browser (`navigator.webdriver`): QA's nightly suites start
   * every run with a clean browser, so a first-run panel would cover every
   * page they test. `/welcome` and the menus still open it there.
   */
  readonly automated?: boolean;
}

/** Open by itself on an admin's first visit, once, on an admin page. */
export function shouldOpenOnFirstRun(i: IntroFirstRunInput): boolean {
  return (
    !!i.userId && i.isAdmin && !!i.url && introMayOpenAt(i.url) && !i.supportSession && !i.seen && !i.automated
  );
}

/** Whether this browser is driven by automation (Playwright, WebDriver). */
export function isAutomatedBrowser(nav: Pick<Navigator, 'webdriver'> | undefined = globalThis.navigator): boolean {
  return nav?.webdriver === true;
}

/**
 * The Settings intro's state: whether it is open, what opened it, and whether
 * this person has seen it.
 *
 * FIRST RUN. `arm()` (called by the shell once its chrome renders for an
 * admitted session) watches the viewer, their admin actions and the settled
 * URL, and opens the intro once when `shouldOpenOnFirstRun` holds. It decides
 * once per person per page load: a person who skips it is not asked again.
 * An automated browser never gets it by itself (QA's suites would meet it
 * on every run); they reach it through `/welcome`.
 *
 * REPLAY. `/welcome` (a guarded route that serves the Overview), the profile
 * menu's "Introduction to Settings" and the ⌘K action all call `replay()`,
 * which opens it from the first page whatever was recorded.
 *
 * SEEN. Finishing or skipping records it per person (`settings-intro-store`).
 */
@Injectable({ providedIn: 'root' })
export class SettingsIntroService {
  private readonly router = inject(Router);
  private readonly identity = inject(ViewerIdentityService);
  private readonly profile = inject(ProfileDataService);
  private readonly support = inject(SupportSessionService);
  private readonly injector = inject(Injector);

  private readonly _open = signal(false);
  private readonly _trigger = signal<IntroTrigger>('first-run');
  /** Bumped on every open, so a replay mounts a fresh panel on its first page. */
  private readonly _opens = signal(0);

  readonly open = this._open.asReadonly();
  readonly trigger = this._trigger.asReadonly();
  readonly opens = this._opens.asReadonly();

  /** The viewer's id: the key the seen flag is kept under. */
  readonly userId = computed(() => this.identity.viewer()?._id ?? null);

  private readonly settledUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    { initialValue: null },
  );

  private armed = false;
  /** The person the first-run decision was made for (it is made once each). */
  private decidedFor: string | null = null;

  /** Start watching for an admin's first visit, and for `/welcome`. Idempotent. */
  arm(): void {
    if (this.armed) return;
    this.armed = true;

    effect(() => {
      const userId = this.userId();
      const url = this.settledUrl();
      const isAdmin = this.profile.canManageSomething();
      const supportSession = !!this.support.session();
      untracked(() => {
        if (!userId || !url || this.decidedFor === userId) return;
        // Wait for the actions: until the viewer has them, an admin reads as not one yet.
        if (!isAdmin) return;
        this.decidedFor = userId;
        const seen = readIntroSeen(userId) !== null;
        const automated = isAutomatedBrowser();
        if (shouldOpenOnFirstRun({ userId, isAdmin, url, supportSession, seen, automated })) this.show('first-run');
      });
    }, { injector: this.injector });

    // `/welcome` asks for the intro: every navigation to it opens it again.
    effect(() => {
      const url = this.settledUrl();
      if (url && url.split(/[?#]/)[0] === INTRO_ROUTE) untracked(() => this.replay('route'));
    }, { injector: this.injector });
  }

  /** Open it again from the first page, whatever was recorded. */
  replay(trigger: Exclude<IntroTrigger, 'first-run'>): void {
    // Opened by hand: the first-run decision is moot for this page load.
    this.decidedFor = this.userId();
    this.show(trigger);
  }

  /**
   * Close it and record that this person has seen it, then go to `next` (a
   * good place to start) when there is one. Leaving `/welcome` for the
   * Overview otherwise, so a reload does not open it again.
   */
  close(how: IntroSeenHow, next?: string): void {
    // Untracked: safe to call from an effect (the read never subscribes it).
    if (!untracked(this._open)) return;
    this._open.set(false);
    writeIntroSeen(this.userId(), how);
    // Leaving /welcome replaces it in history, so Back never opens it again.
    const onWelcome = this.router.url.split(/[?#]/)[0] === INTRO_ROUTE;
    if (next) void this.router.navigateByUrl(next, { replaceUrl: onWelcome });
    else if (onWelcome) void this.router.navigateByUrl('/overview', { replaceUrl: true });
  }

  private show(trigger: IntroTrigger): void {
    if (untracked(this._open)) return;
    this._trigger.set(trigger);
    this._opens.update((n) => n + 1);
    this._open.set(true);
  }
}
